/**
 * Centralized client-side logger.
 *
 * Use this instead of `console.*` everywhere. In production builds (NODE_ENV
 * === 'production') only `error` survives — `log`, `info`, `warn`, `debug` are
 * silenced. Optionally forward errors to a monitoring service later by
 * implementing `forwardToMonitoring` below.
 *
 * Adoption strategy: existing 486 `console.*` callsites can be migrated
 * incrementally. New code should import from this module:
 *
 *   import logger from '../utils/logger';
 *   logger.info('Loaded data', data);
 *   logger.error('Login failed', err);
 */
const isProd = process.env.NODE_ENV === 'production';

const noop = () => {};

const safeBind = (method) => {
  if (typeof console === 'undefined' || typeof console[method] !== 'function') {
    return noop;
  }
  return console[method].bind(console);
};

// In production, swallow non-error noise but always preserve errors.
const logger = {
  log: isProd ? noop : safeBind('log'),
  info: isProd ? noop : safeBind('info'),
  warn: isProd ? noop : safeBind('warn'),
  debug: isProd ? noop : safeBind('debug'),
  error: safeBind('error'),
};

/**
 * Hook for sending errors to a monitoring service (Sentry, LogRocket, etc.).
 * Currently a stub — wire up when an error-tracking provider is added.
 */
// eslint-disable-next-line no-unused-vars
export const forwardToMonitoring = (err, context = {}) => {
  // Intentionally left empty — replace with provider SDK when adopted.
};

export default logger;
